// A pro forma for each option: what it costs to build and what it sells for.
//
// Deliberately simple and entirely the developer's numbers: cost per m²
// above and below ground, soft costs and contingency as shares of the hard
// cost, sale price per m² of usable area by flat type, shops and parking
// spaces. The defaults are placeholders to be overwritten, not market data,
// and the page says so; what matters is that every number on screen moves
// with the geometry, so a change of depth or a derogation shows up in the
// margin at once.
import type { Generated } from "./generate.ts";
import type { UnitType } from "./rules.ts";

export interface FinanceInputs {
  /** The plot, all in, €. */
  land: number;
  /** Hard cost per m² of above-ground built area (Adc), €. */
  costAbove: number;
  /** Hard cost per m² of basement, €. */
  costBelow: number;
  /** Design, permits, fees, financing: share of the hard cost. */
  softPct: number;
  contingencyPct: number;
  /** Sale price per m² of usable area, by flat type, €. */
  salePerM2: Record<UnitType, number>;
  /** Shops: sale price per m² of built area, €. */
  retailPerM2: number;
  /** One parking space, sold, €. */
  parkingSpace: number;
}

/** Placeholders. They have the right order of magnitude for Bucharest and nothing more. */
export const DEFAULT_FINANCE: FinanceInputs = {
  land: 0,
  costAbove: 1100,
  costBelow: 650,
  softPct: 12,
  contingencyPct: 5,
  salePerM2: { "1": 2400, "2": 2250, "3": 2150, "4": 2050, "5": 2000 },
  retailPerM2: 2000,
  parkingSpace: 15000,
};

export interface ProForma {
  revenue: { flats: number; retail: number; parking: number; total: number };
  cost: { land: number; hardAbove: number; hardBelow: number; soft: number; contingency: number; total: number };
  profit: number;
  /** Profit over revenue. */
  margin: number;
  /** Profit over total cost. */
  onCost: number;
  costPerFlat: number;
  /** Revenue per m² of plot: what the land is worth to this scheme. */
  revenuePerPlotM2: number;
}

/**
 * What the plot is worth to this scheme: the land price at which the margin
 * comes out at `target` (a share of revenue). Below it the deal clears the
 * target; above it, it does not. The question a developer asks first.
 */
export function residualLand(g: Generated, plotArea: number, f: FinanceInputs, target: number): number {
  const p = proForma(g, plotArea, { ...f, land: 0 });
  return p.revenue.total * (1 - target) - p.cost.total;
}

/** Profit as sale prices and build costs move by ±`step`: rows are prices, columns costs. */
export function sensitivity(g: Generated, plotArea: number, f: FinanceInputs, step = 0.1): number[][] {
  const scale = (k: number) => Object.fromEntries(Object.entries(f.salePerM2).map(([t, v]) => [t, v * k])) as FinanceInputs["salePerM2"];
  return [-1, 0, 1].map((i) => [-1, 0, 1].map((j) => proForma(g, plotArea, {
    ...f, salePerM2: scale(1 + i * step), retailPerM2: f.retailPerM2 * (1 + i * step), parkingSpace: f.parkingSpace * (1 + i * step),
    costAbove: f.costAbove * (1 + j * step), costBelow: f.costBelow * (1 + j * step),
  }).profit));
}

export function proForma(g: Generated, plotArea: number, f: FinanceInputs): ProForma {
  const flats = g.layout.floors.flatMap((x) => x.units).filter((u) => u.kind === "apartment");
  const retail = g.layout.floors.flatMap((x) => x.units).filter((u) => u.kind === "retail");
  const rFlats = flats.reduce((t, u) => t + (u.usable ?? 0) * (f.salePerM2[u.type!] ?? 0), 0);
  const rRetail = retail.reduce((t, u) => t + u.area * f.retailPerM2, 0);
  const rParking = g.parking.capacity * f.parkingSpace;
  const revenue = rFlats + rRetail + rParking;

  const hardAbove = g.option.grossArea * f.costAbove;
  const hardBelow = g.parking.basementArea * g.parking.levels * f.costBelow;
  const hard = hardAbove + hardBelow;
  const soft = (hard * f.softPct) / 100;
  const contingency = (hard * f.contingencyPct) / 100;
  const total = f.land + hard + soft + contingency;
  const profit = revenue - total;
  return {
    revenue: { flats: rFlats, retail: rRetail, parking: rParking, total: revenue },
    cost: { land: f.land, hardAbove, hardBelow, soft, contingency, total },
    profit,
    margin: revenue ? profit / revenue : 0,
    onCost: total ? profit / total : 0,
    costPerFlat: flats.length ? total / flats.length : 0,
    revenuePerPlotM2: plotArea ? revenue / plotArea : 0,
  };
}
